import './Tooltip.css';
import '../style.css';

import React from 'react';
import { Navigate, Route, useLocation } from 'react-router-dom';
import { AuthorisedRoute, AuthRoutes, ErrorBoundary, NotFound, useApplicationContext } from '@economic/react-app';
import { Layout, useIsLargeScreen } from '@economic/taco';
import { AppDrawer } from '~/app/components/AppDrawer/AppDrawer';
import DialogProvider from '~/app/shared/components/Dialog/DialogProvider';
import Header from './Header';
import { sidebars, content as routes } from '../pages';
import isWindowUndocked from '../shared/util/utilities/isWindowUndocked';
import Loader from '../shared/components/Loader';
import { DataProvider } from '../shared/components/DataContext';
import { DialogManagerProvider } from '../shared/components/DialogManagerContext';
import { useEconomic } from './useEconomic';
import TrialExpiredDialog from '../shared/components/modals/TrialExpiredDialog';
import VismaConnectCongratsDialog from '../shared/components/modals/VismaConnectCongratsDialog';
import VismaConnectJoinDialog from '../shared/components/modals/VismaConnectJoinDialog';
import RequestRemoteAccessDialog from '../shared/components/modals/RequestRemoteAccessDialog';
import { useIntroductionTour } from './Header/IntroductionTour/useIntroductionTour';
import GettingStartedDialog from '../forms/GettingStartedSplash/GettingStartedDialog';
import LegacyDialogRoutes from '../shared/LegacyDialogRoutes';
import { useUnless } from './useUnless';
import { WootricSurveyModalWithErrorBoundary } from '../shared/components/Wootric';
import { useEcopediaUrl } from './Header/hooks/useEcopediaUrl';
import { Sidebar } from './Sidebar';

function BaseRoute() {
    const { configuration } = useApplicationContext();
    const baseRoute = configuration.routes['/'];

    if (baseRoute && baseRoute.startsWith('/')) {
        return <Navigate to={baseRoute} />;
    }
    if (baseRoute === 'AppStore/developer') {
        // temporary fix
        return <Navigate to="developer" />;
    }

    return <Navigate to="home" />;
}

function EconomicLayout() {
    const showHeader = isWindowUndocked() === false;
    const { pathname } = useLocation();
    const baseRoute = pathname.split('/')[1];
    const ecopediaLink = useEcopediaUrl();
    const isLargeScreen = useIsLargeScreen();
    useUnless();

    // resetting sidebar on route changes closes it, but we don't want it to occur when not responsive
    // because it resets the scroll position inside the navigation
    const sidebarKey = isLargeScreen ? baseRoute : pathname;

    return (
        <Layout className="[body.firebird\_\_overlay--open_&]:z-0">
            {showHeader ? (
                <Layout.Top>
                    {({ toggleSidebar }) => <Header toggleSidebar={toggleSidebar} ecopediaLink={ecopediaLink} />}
                </Layout.Top>
            ) : null}
            <Layout.Page>
                <Layout.Sidebar id="navigation" className="z-[9]" key={sidebarKey} children={Sidebar} />
                <Layout.Content className={sidebars[baseRoute] ? 'p-4' : undefined}>
                    <ErrorBoundary>
                        <AuthRoutes>
                            <AuthorisedRoute path="/" element={<BaseRoute />} />

                            <AuthorisedRoute path="accounting/*" element={<routes.Accounting />} />
                            <AuthorisedRoute path="admin/*" element={<routes.Admin />} />
                            <AuthorisedRoute path="basic/*" element={<routes.Basic />} />
                            <AuthorisedRoute path="custom/*" element={<routes.Custom />} />
                            <AuthorisedRoute path="developer" element={<routes.Developer />} />
                            <AuthorisedRoute path="home" element={<routes.Home />} />
                            <AuthorisedRoute path="inventory/*" element={<routes.Inventory />} />
                            <AuthorisedRoute path="journal-pro" element={<routes.JournalPro />} />
                            <AuthorisedRoute path="market/*" element={<routes.Market />} />
                            <AuthorisedRoute path="partner-api" element={<routes.PartnerApi />} />
                            <AuthorisedRoute path="projects/*" element={<routes.Projects />} />
                            <AuthorisedRoute path="reports/*" element={<routes.Reports />} />
                            <AuthorisedRoute path="sales/*" element={<routes.Sales />} />
                            <AuthorisedRoute path="settings/*" element={<routes.Settings />} />
                            <AuthorisedRoute path="system/*" element={<routes.System />} />
                            <AuthorisedRoute path="user/*" element={<routes.User />} />
                            <AuthorisedRoute path="charlie" element={<routes.Charlie />} />

                            {/* modules */}
                            <AuthorisedRoute path="inbox/*" element={<routes.Inbox />} />

                            <AuthorisedRoute path="logout" element={<routes.Logout />} />

                            <Route path="404" element={<NotFound />} />
                            <Route path="*" element={<NotFound />} />
                        </AuthRoutes>

                        <TrialExpiredDialog />
                        <GettingStartedDialogWrapper />
                        <VismaConnectCongratsDialog />
                        <VismaConnectJoinDialog />
                        <RequestRemoteAccessDialog />
                        <WootricSurveyModalWithErrorBoundary />

                        <LegacyDialogRoutes />
                    </ErrorBoundary>
                </Layout.Content>
                <AppDrawer />
            </Layout.Page>
        </Layout>
    );
}

export default function Economic() {
    const loading = useEconomic();

    if (loading) {
        return <Loader />;
    }

    return (
        <DataProvider>
            <DialogProvider>
                <DialogManagerProvider>
                    <EconomicLayout />
                </DialogManagerProvider>
            </DialogProvider>
        </DataProvider>
    );
}

/**
 * Wrap GettingStartedDialog and trigger home tour after it is done.
 * This component must be under EContext context provider.
 */
function GettingStartedDialogWrapper() {
    const [, setShowTour] = useIntroductionTour();

    function gettingStartedFinished() {
        setShowTour(true);
    }

    return <GettingStartedDialog onDone={gettingStartedFinished} />;
}
